import type {
  StreamEvent,
  StreamEventType,
  Turn,
} from './types';

// API base URL resolution order:
//   1. window.VITE_API_URL — set at runtime by the widget shell (chat.html reads
//      a query-string param so the same bundle works behind different hosts).
//   2. import.meta.env.VITE_API_URL — baked in at build time for the standalone
//      web-ui deploy.
//   3. Empty string → same-origin (Vite dev-server proxy).
declare global {
  interface Window {
    VITE_API_URL?: string;
  }
}
const API_URL: string =
  (typeof window !== 'undefined' ? window.VITE_API_URL : undefined) ??
  (import.meta.env.VITE_API_URL as string | undefined) ??
  '';

export interface QueryRequest {
  query: string;
  prior_turns?: Turn[];
}

export interface StreamCallbacks {
  onEvent: (event: StreamEvent) => void;
  onError?: (err: Error) => void;
  signal?: AbortSignal;
}

// Watchdog: if no SSE bytes arrive for this long, treat the stream as dead.
// Lambda cold start + first Bedrock token can take ~10s under load; 60s leaves
// plenty of headroom while still preventing the UI from hanging forever when
// the backend silently stalls (e.g., ELB idle timeout severs the TCP socket
// without flushing fetch's reader). Reset on every successful read.
const STREAM_IDLE_TIMEOUT_MS = 60_000;

/**
 * Stream a query against the orchestrator's /query_stream SSE endpoint.
 *
 * Uses fetch + ReadableStream — NOT EventSource — because EventSource only
 * supports GET, and our endpoint is POST (request body carries the query).
 *
 * Anonymous: no auth header, no session id, no cookies, no localStorage reads.
 */
export async function streamQuery(req: QueryRequest, cb: StreamCallbacks): Promise<void> {
  const url = `${API_URL.replace(/\/+$/, '')}/query_stream`;

  // Compose the caller's AbortSignal with our idle-watchdog signal so a stalled
  // backend produces a clean abort instead of an indefinite "looking up" UI.
  const idleCtl = new AbortController();
  const composedSignal: AbortSignal = composeSignals(cb.signal, idleCtl.signal);
  let idleTimer: ReturnType<typeof setTimeout> | null = null;
  const resetIdle = () => {
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(() => idleCtl.abort(), STREAM_IDLE_TIMEOUT_MS);
  };
  resetIdle();

  let resp: Response;
  try {
    resp = await fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(req),
      signal: composedSignal,
      // No credentials — fully anonymous.
      credentials: 'omit',
    });
  } catch (e) {
    if (idleTimer) clearTimeout(idleTimer);
    if (idleCtl.signal.aborted && !cb.signal?.aborted) {
      cb.onError?.(new Error('Stream timed out — no response from server.'));
    } else {
      cb.onError?.(e instanceof Error ? e : new Error(String(e)));
    }
    return;
  }

  if (!resp.ok || !resp.body) {
    if (idleTimer) clearTimeout(idleTimer);
    cb.onError?.(new Error(`HTTP ${resp.status} ${resp.statusText}`));
    return;
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buf = '';

  try {
    // SSE frame: "event: <type>\ndata: <json>\n\n"
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      resetIdle();
      buf += decoder.decode(value, { stream: true });
      let idx: number;
      while ((idx = buf.indexOf('\n\n')) !== -1) {
        const frame = buf.slice(0, idx);
        buf = buf.slice(idx + 2);
        const ev = parseFrame(frame);
        if (ev) cb.onEvent(ev);
      }
    }
    if (buf.trim()) {
      const ev = parseFrame(buf);
      if (ev) cb.onEvent(ev);
    }
  } catch (e) {
    if (idleCtl.signal.aborted && !cb.signal?.aborted) {
      cb.onError?.(new Error('Stream timed out — connection went idle.'));
    } else {
      cb.onError?.(e instanceof Error ? e : new Error(String(e)));
    }
  } finally {
    if (idleTimer) clearTimeout(idleTimer);
  }
}

function composeSignals(...signals: (AbortSignal | undefined)[]): AbortSignal {
  const ctl = new AbortController();
  for (const s of signals) {
    if (!s) continue;
    if (s.aborted) {
      ctl.abort();
      break;
    }
    s.addEventListener('abort', () => ctl.abort(), { once: true });
  }
  return ctl.signal;
}

function parseFrame(frame: string): StreamEvent | null {
  let type: StreamEventType | null = null;
  let data: string | null = null;
  for (const line of frame.split('\n')) {
    if (line.startsWith('event:')) type = line.slice('event:'.length).trim() as StreamEventType;
    else if (line.startsWith('data:')) data = (data ?? '') + line.slice('data:'.length).trim();
  }
  if (!type || data === null) return null;
  try {
    return { type, data: JSON.parse(data) as unknown };
  } catch {
    return { type, data };
  }
}

export async function postRating(
  interaction_id: string,
  rating: 'up' | 'down',
  comment?: string,
): Promise<void> {
  const url = `${API_URL.replace(/\/+$/, '')}/rating`;
  await fetch(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ interaction_id, rating, comment }),
    credentials: 'omit',
  });
}
